// Steps through a user-supplied video file at a fixed sample rate and
// yields ImageData for each sample point. Uses `<video>.currentTime = t`
// + `seeked`-await, which works everywhere including Safari; WebCodecs
// (VideoDecoder + MediaSource) is a follow-up upgrade for Chromium where
// it can deliver higher throughput. Results are annotated with the
// method used so a future WebCodecs implementation can be A/B compared.
//
// Memory: only one frame is held at a time — the shared canvas is reused.
// Reasonable video files even at 60 s / 30 fps produce ~1800 frames, so
// caching all decoded ImageData would blow the heap (>10 GB uncompressed).

export interface FrameSample {
  /** 0-based frame index across the iteration. */
  index: number;
  /** Video time in seconds at the sample point. */
  timeSeconds: number;
  /** Total frames the iterator plans to emit — known once metadata loads. */
  totalFrames: number;
  /** Decoded RGBA frame. Reuses a shared buffer — copy if the caller retains. */
  imageData: ImageData;
}

export interface IterateOptions {
  file: File;
  /** Sample rate in hz. Defaults to 30 fps (matches most phone cameras). */
  sampleRateHz?: number;
  /** Hard cap on source video seconds processed. Defaults to 60 s. */
  maxSeconds?: number;
  /** Downscale frames so long edge ≤ this (matches the live sampler default). */
  targetLongEdgePx?: number;
  signal?: AbortSignal;
}

const DEFAULT_SAMPLE_RATE_HZ = 30;
const DEFAULT_MAX_SECONDS = 60;
const DEFAULT_LONG_EDGE_PX = 1600;

export async function* iterateVideoFrames(
  opts: IterateOptions
): AsyncGenerator<FrameSample, { cappedAtMax: boolean; totalFrames: number }, void> {
  const sampleRate = opts.sampleRateHz ?? DEFAULT_SAMPLE_RATE_HZ;
  const maxSeconds = opts.maxSeconds ?? DEFAULT_MAX_SECONDS;
  const longEdge = opts.targetLongEdgePx ?? DEFAULT_LONG_EDGE_PX;

  const url = URL.createObjectURL(opts.file);
  const video = document.createElement('video');
  video.src = url;
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.crossOrigin = 'anonymous';

  try {
    await onceEvent(video, 'loadedmetadata');
    const durationSec = Math.min(video.duration || 0, maxSeconds);
    const frameCount = Math.max(1, Math.floor(durationSec * sampleRate));
    const cappedAtMax = video.duration > maxSeconds;

    const scale = Math.min(1, longEdge / Math.max(video.videoWidth, video.videoHeight));
    const targetW = Math.max(1, Math.round(video.videoWidth * scale));
    const targetH = Math.max(1, Math.round(video.videoHeight * scale));
    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(targetW, targetH)
        : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH });
    const ctx = canvas.getContext('2d') as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;
    if (!ctx) throw new Error('2d context unavailable for video frame extraction');

    for (let i = 0; i < frameCount; i++) {
      if (opts.signal?.aborted) throw new DOMException('cancelled', 'AbortError');
      const t = i / sampleRate;
      await seekAndAwait(video, t);
      ctx.drawImage(video, 0, 0, targetW, targetH);
      const imageData = ctx.getImageData(0, 0, targetW, targetH);
      yield { index: i, timeSeconds: t, totalFrames: frameCount, imageData };
    }

    return { cappedAtMax, totalFrames: frameCount };
  } finally {
    video.src = '';
    URL.revokeObjectURL(url);
  }
}

function onceEvent(target: HTMLVideoElement, name: string): Promise<Event> {
  return new Promise((resolve, reject) => {
    const onEvt = (e: Event) => {
      target.removeEventListener(name, onEvt);
      target.removeEventListener('error', onErr);
      resolve(e);
    };
    const onErr = () => {
      target.removeEventListener(name, onEvt);
      target.removeEventListener('error', onErr);
      reject(new Error(target.error?.message ?? `video error waiting for ${name}`));
    };
    target.addEventListener(name, onEvt);
    target.addEventListener('error', onErr);
  });
}

// Seek then await 'seeked'. Race-safe: listeners are attached BEFORE
// setting currentTime (so a synchronous seek — cached keyframe, t=0 —
// still resolves), and a sync-fired seek is caught by the post-attach
// check for `!video.seeking`. Also surfaces the actual MediaError.message
// on error instead of an opaque "video error" string.
function seekAndAwait(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve, reject) => {
    let done = false;
    const cleanup = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
    const onSeeked = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve();
    };
    const onError = () => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error(video.error?.message ?? `video error waiting for seek to ${t}s`));
    };
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    video.currentTime = t;
    // If the seek resolved synchronously (cached keyframe, t=0), 'seeked'
    // may have fired before the listener attached in some engines; the
    // `!seeking` check below catches that fast path.
    if (!video.seeking && Math.abs(video.currentTime - t) < 1e-3) {
      onSeeked();
    }
  });
}
